import React, { useEffect, useCallback, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDesign } from "../../../context/DesignContext";
import { useContentLayout } from "../../../context/ContentMaxWidthPercentContext";
import { useMeasuredGridColumnCount } from "../../../Utils/contentWidthGridLayout";
import { isCreatorPagesMode } from "../../../Utils/componentsTable";
import InfiniteScroll from "react-infinite-scroll-component";
import { getComponentViewTransitionName } from "../../../Utils/viewTransitions";
import { visibleCarouselDescription, normalizeSoundValue } from "../../../Utils/helpers/carouselHelper";
import { shouldUseEmbedLoadMoreButton } from "../../../Utils/embed/openCollectionNavigation";
import { requestEmbedIframeHeightRecalcFor } from "../../../Utils/HelperFunction/Useiframeheight";
import ClipLoader from "react-spinners/ClipLoader";
import { TemplateNewsletterFeedTile } from "../../../Screens/DashboardView/MasterTemplateEditor/templatePages/TemplateNewsletterFeedTile";
import { useIndividualPageLayout } from "./IndividualPageLayoutContext";
import CarouselPostMedia from "../../CarouselStructure/CarouselPostMedia";
import { isContentDayWindowComponentType } from "../../../Utils/media/feedMedia";
import {
  MASTER_TEMPLATE_SCROLL_ROOT_ID,
  resolveFeedScrollableTargetId,
} from "../../../Utils/feedScrollRoot";
import "./RenderIndividualUI.css";

/**
 * Same sound gate as legacy LiB / PostTemplate:
 * saved `content_sound` when set, otherwise app default On (StaticVariables).
 */
export function resolveFeedSoundOn(
  _from: string | undefined,
  contentSound: unknown,
): boolean {
  return normalizeSoundValue(contentSound, true);
}

export type NormalizedContentFilter = "both" | "content" | "products" | "post";

export function normalizeShoppableContentFilter(
  filter: unknown
): NormalizedContentFilter {
  const raw = String(filter ?? "both")
    .toLowerCase()
    .trim();
  if (raw === "content") return "content";
  if (raw === "products" || raw === "product") return "products";
  if (raw === "post") return "post";
  return "both";
}

function RenderIndividualUI(props: any) {
  const {
    data,
    currentIndex,
    title,
    handleNavigate,
    component,
    handleNextPage,
    handlePrevPage,
    containerRef,
    handleRedirect,
    brand_color,
    type,
    isLinks,
    componentType,
    from,
    username,
    user_id,
    isPodcast,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    expandedSlot,
    designTokens:overrideDesignTokens,
  } = props;
  

  const { maxWidthPercent, narrowRemLayout, embedShellLayout } = useContentLayout();
  const isCreatorPage = isCreatorPagesMode(from);
  const isEmbed =
    String(from || "").toLowerCase() === "embed-search" &&
    Boolean(embedShellLayout);
  const gridColumnCount = useMeasuredGridColumnCount(containerRef, {
    maxWidthPercent,
    narrowRemLayout,
    isCreatorPage,
    isEmbed,
  });

  const navigate = useNavigate();
  const location = useLocation();
  const { fontFamily, primaryColor, secondaryColor, tertiaryColor, templateName, secondaryFont, primaryFont, forthColor } =
    useDesign();
  const designTokens = {
    fontFamily,
    primaryColor,
    secondaryColor,
    primaryFontFamily: overrideDesignTokens?.primaryFontFamily ?? primaryFont,
    secondaryFontFamily: overrideDesignTokens?.secondaryFontFamily ?? secondaryFont,
    tertiaryColor,
    templateName,
    forthColor,
  };

  const feedSoundOn = resolveFeedSoundOn(from, component?.content_sound);

  // `/website/:user/link-in-bio/:slug` has no `:id` param (unlike `/:username/:id`).
  // IndividualComponentLoader also sets context so dashboard preview matches.
  const fromIndividualLoader = useIndividualPageLayout();
  const isWebsiteLibIndividual = /\/website\/[^/]+\/link-in-bio\/[^/]+/i.test(
    location.pathname,
  );
  const isWebsiteTemplate = from === "website-template";

  const contentFilter = normalizeShoppableContentFilter(
    component?.shoppable_content_filter
  );
  const carouselDescription = visibleCarouselDescription(component, {
    from,
    isIndividualPage: true,
  });

  const useEmbedLoadMore = shouldUseEmbedLoadMoreButton(from);
  // Grid layout for all standalone individual-page views; carousel only for inline landing-page
  // expansions (expandedSlot===true). This covers both:
  //   • preview  /preview/:username/:id  (param.id is set, expandedSlot is false)
  //   • live     /:username/:pageSlug    (param.id is undefined, expandedSlot is false)
  // The inline expanded section inside the landing page passes expandedSlot=true explicitly
  // and should keep the horizontal carousel layout.
  const isDetailView = expandedSlot !== true;
  // Pagination is allowed regardless of route as long as the loader supplies fetchNextPage.
  const canPaginate = !expandedSlot && typeof fetchNextPage === "function";
  const hasMorePages = Boolean(hasNextPage);

  useEffect(() => {
    if (!useEmbedLoadMore) return;
    requestEmbedIframeHeightRecalcFor(from);
  }, [useEmbedLoadMore, from, data?.length, isFetchingNextPage]);

  // InfiniteScroll defaults to window; website pages scroll
  // `#master-template-scroll-root` instead. Prefer that known root, then walk
  // up for modal/sidebar/embed wrappers.
  const [infiniteScrollTargetId, setInfiniteScrollTargetId] = useState<
    string | undefined
  >(undefined);

  useEffect(() => {
    if (!canPaginate || useEmbedLoadMore) {
      setInfiniteScrollTargetId(undefined);
      return;
    }
    if (typeof window === "undefined") return;

    const knownTarget = resolveFeedScrollableTargetId(
      isWebsiteTemplate || isWebsiteLibIndividual
        ? MASTER_TEMPLATE_SCROLL_ROOT_ID
        : undefined
    );
    if (knownTarget) {
      setInfiniteScrollTargetId(knownTarget);
      return;
    }

    const start = (containerRef?.current as HTMLElement | null) ?? null;
    if (!start) {
      setInfiniteScrollTargetId(undefined);
      return;
    }

    const isScrollable = (el: HTMLElement) => {
      const oy = window.getComputedStyle(el).overflowY;
      return oy === "auto" || oy === "scroll";
    };

    let node: HTMLElement | null = start.parentElement;
    let found: HTMLElement | null = null;
    while (
      node &&
      node !== document.body &&
      node !== document.documentElement
    ) {
      if (isScrollable(node)) {
        found = node;
        break;
      }
      node = node.parentElement;
    }

    if (found) {
      if (!found.id) {
        found.id = `inf-scroll-target-${Math.random()
          .toString(36)
          .slice(2, 9)}`;
      }
      setInfiniteScrollTargetId(found.id);
    } else {
      setInfiniteScrollTargetId(undefined);
    }
  }, [
    canPaginate,
    useEmbedLoadMore,
    containerRef,
    data?.length,
    isWebsiteTemplate,
    isWebsiteLibIndividual,
  ]);

  const getProductName = useCallback(
    (post: any) => {
      if (componentType == "content" || componentType == "query-content") {
        return post?.content_title || post?.product_name || null;
      } else if (componentType === "product-bank") {
        return post?.shop_title || post?.table_name;
      } else if (post?.shop_title) {
        return post.shop_title;
      } else if (post?.content_title) {
        return post.content_title;
      } else if (post?.product_name) {
        return post.product_name;
      } else if (post.table_name) {
        return post.table_name;
      }
      return null;
    },
    [componentType]
  );

  useEffect(() => {
    if (
      useEmbedLoadMore ||
      isWebsiteTemplate ||
      fromIndividualLoader ||
      typeof fetchNextPage !== "function" ||
      !hasNextPage ||
      isFetchingNextPage ||
      !data?.length
    ) {
      return;
    }

    const buffer = 15;
    const itemsLeft = data.length - (currentIndex + 3);
    if (itemsLeft <= buffer) {
      fetchNextPage();
    }
  }, [
    useEmbedLoadMore,
    currentIndex,
    data?.length,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  ]);

  const renderPostCell = useCallback(
    (link: any, index: number = 0) => {
      if (isWebsiteTemplate && link?.platform === "newsletter") {
        return <TemplateNewsletterFeedTile post={link} />;
      }

      const mediaComponentType = String(
        componentType ||
          component?.type ||
          component?.display_name ||
          type ||
          "",
      );
      const isContentDayWindow =
        isContentDayWindowComponentType(mediaComponentType) ||
        mediaComponentType === "content" ||
        mediaComponentType === "query-content";

      return (
        <CarouselPostMedia
          variant="individual"
          link={link}
          handleShop={handleRedirect}
          title={title}
          brand_color={brand_color}
          isLinks={isLinks}
          productName={getProductName(link)}
          contentShape={component?.content_shape}
          isSoundOn={feedSoundOn}
          type={link?.type || type}
          componentType={mediaComponentType}
          pinnedPostIds={component?.pinned_posts?.split(",") || []}
          isDashboard={false}
          designTokens={designTokens}
          from={from}
          mediaIndex={index}
          isPodcast={isPodcast || link?.platform == "podcast"}
          treatProductTypeAsStill={!isContentDayWindow}
          forceProductFrame={
            !isContentDayWindow &&
            (String(link?.type || "").toUpperCase() === "PRODUCT" ||
              mediaComponentType === "product-bank")
          }
        />
      );
    },
    [
      isPodcast,
      componentType,
      component,
      brand_color,
      handleRedirect,
      title,
      isLinks,
      type,
      from,
      designTokens,
      getProductName,
      feedSoundOn,
      isWebsiteTemplate,
    ]
  );

  const outerContainerClass = [
    "individual-outer-container",
    component.content_shape === "sq" ? "square-grid" : "",
    isDetailView
      ? "render-individual-ui__outer--detail"
      : "outer-container-reels",
    isWebsiteTemplate ? "mte-tpl-grid-enter-host" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const gridClassName = `render-individual-ui__grid render-individual-ui__grid--filter-${contentFilter}`;

  const renderGrid = () => (
    <div className={gridClassName} data-grid-columns={gridColumnCount}>
      {data &&
        data.map((link: any, index: number) => (
          <div
            key={link.id}
            className={[
              "render-individual-ui__cell",
              link.platform == "youtube" ? "youtube-link-position" : "",
              isWebsiteTemplate ? "mte-tpl-grid-tile" : "",
            ]
              .filter(Boolean)
              .join(" ")}
            style={
              isWebsiteTemplate
                ? ({
                    ["--mte-tile-stagger" as string]: String(Math.min(index, 23)),
                  } as React.CSSProperties)
                : undefined
            }
          >
            {renderPostCell(link, index)}
          </div>
        ))}
    </div>
  );

  const loadMoreButton =
    canPaginate && useEmbedLoadMore && hasMorePages ? (
      <div className="render-individual-ui__load-more-wrap">
        <button
          type="button"
          className="view-more-button render-individual-ui__load-more-btn"
          onClick={() => {
            if (!isFetchingNextPage && typeof fetchNextPage === "function") {
              fetchNextPage();
            }
          }}
          disabled={isFetchingNextPage}
          style={{
            display: "block",
            margin: "24px auto 0",
            minWidth: "100px",
            cursor: isFetchingNextPage ? "not-allowed" : "pointer",
            pointerEvents: isFetchingNextPage ? "none" : "auto",
            textAlign: "center",
            padding: "10px 16px",
            color:
              designTokens?.templateName === "Default"
                ? "#fff"
                : designTokens?.primaryColor || "#fff",
            backgroundColor:
              designTokens?.templateName === "Default"
                ? "#000"
                : designTokens?.secondaryColor,
            border:
              designTokens?.templateName === "Default"
                ? "none"
                : designTokens?.secondaryColor
                  ? `1px solid ${designTokens.secondaryColor}`
                  : "none",
            borderRadius: "0.5rem",
            fontFamily: designTokens?.secondaryFontFamily,
            fontSize: "12px",
            opacity: 1,
          }}
        >
          {isFetchingNextPage ? (
            <ClipLoader
              loading={true}
              size={16}
              color={
                designTokens?.templateName === "Default"
                  ? "#fff"
                  : designTokens?.primaryColor || "#fff"
              }
            />
          ) : (
            "Load More"
          )}
        </button>
      </div>
    ) : null;

  const renderPaginatedContent = () => {
    if (!canPaginate) {
      return renderGrid();
    }

    // Website LiB individual routes pass from="landing-page" but still scroll
    // inside `#master-template-scroll-root` — never rely on window alone.
    const resolvedScrollTarget =
      infiniteScrollTargetId ||
      (isWebsiteTemplate || isWebsiteLibIndividual
        ? MASTER_TEMPLATE_SCROLL_ROOT_ID
        : undefined);
    const scrollTargetProps = resolvedScrollTarget
      ? {
          scrollableTarget: resolvedScrollTarget,
          style: { overflow: "visible" as const, height: "auto" as const },
        }
      : {};

    if (from?.toLowerCase() === "landing-page") {
      return (
        <InfiniteScroll
          key={resolvedScrollTarget || "window"}
          dataLength={data.length}
          next={fetchNextPage}
          hasMore={hasMorePages}
          loader={
            <div className="render-individual-ui__infinite-loader">
              <ClipLoader
                color="#5274f9"
                loading={isFetchingNextPage && !isWebsiteTemplate}
                className="feed-loader"
                size={30}
              />
            </div>
          }
          scrollThreshold={0.85}
          {...scrollTargetProps}
        >
          {renderGrid()}
        </InfiniteScroll>
      );
    }

    if (useEmbedLoadMore) {
      return (
        <div className="render-individual-ui__paginated-stack">
          {renderGrid()}
          {loadMoreButton}
        </div>
      );
    }

    return (
      <InfiniteScroll
        key={resolvedScrollTarget || "window"}
        dataLength={data.length}
        next={fetchNextPage}
        hasMore={hasMorePages}
        loader={
          <div className="render-individual-ui__infinite-loader">
            <ClipLoader
              color="#5274f9"
              loading={isFetchingNextPage && !isWebsiteTemplate}
              className="feed-loader"
              size={30}
            />
          </div>
        }
        scrollThreshold={0.85}
        {...scrollTargetProps}
      >
        {renderGrid()}
      </InfiniteScroll>
    );
  };

  return (
    <div
      className={
        narrowRemLayout && String(from || "").toLowerCase() !== "embed-search"
          ? "feed-optimized-width-no-page"
          : ""
      }
    >
      <div
        className={`render-individual-ui render-individual-ui--filter-${contentFilter}`}
      >
      {data && data.length ? (
        <div>
          <div className="mainDiv">
            {!isWebsiteTemplate && (
            <div className="component-header-individual">
              <p
                className={`component-name-individual${
                  isDetailView ? " component-name-individual--static" : ""
                }`}
                style={{
                  viewTransitionName: getComponentViewTransitionName(component),
                  fontFamily: designTokens?.primaryFontFamily,
                  ...(isDetailView || fromIndividualLoader ? { cursor: "auto" } : {}),
                }}
                onClick={
                  isDetailView
                    ? undefined
                    : () =>
                        handleNavigate(
                          navigate,
                          location,
                          username,
                          user_id,
                          component,
                          from
                        )
                }
              >
                {title}
              </p>
              {carouselDescription ? (
                <p
                  className="component-description-individual"
                  style={{
                    fontFamily: designTokens?.secondaryFontFamily,
                  }}
                >
                  {carouselDescription}
                </p>
              ) : null}
            </div>
            )}
          </div>
          <div className={outerContainerClass} ref={containerRef}>
            {renderPaginatedContent()}
          </div>
        </div>
      ) : null}
      </div>
    </div>
  );
}

export default RenderIndividualUI;
